// Scheduler — 1:1 portiert aus V1 App.js `getNextScheduledEvent`.
//
// Verantwortung: ermittelt den nächsten autonomen Agent-Run (Morgenbriefing,
// Marktupdate, Lagebild, Wochenrückblick, Breaking) basierend auf:
// - `agentSchedule[key] === 'auto'`
// - `agentTime[key]` (HH:MM in CET/CEST)
//
// V1-Verhalten: alle Zeiten in Europe/Berlin, Sommerzeit-korrekt. Wochenenden
// werden nach Vendor-Logik berücksichtigt (Sa/So → Montag).

import type { AgentKey, EveProfile } from '@/types/eve-profile';

/** Auto-Agent-Keys die V1 schedulebar sind. */
export const SCHEDULABLE_AGENTS = [
  'morgenbriefing',
  'marktupdate',
  'lagebild',
  'wochenendrückblick',
  'breaking',
] as const satisfies ReadonlyArray<AgentKey>;

export interface ScheduledEvent {
  label: string;
  agentKey: AgentKey | null;
  /** Sekunden bis zum nächsten Trigger. `null` wenn nichts geplant ist. */
  seconds: number | null;
}

/**
 * Parsed `HH:MM` in Europe/Berlin → Date-Objekt im lokalen Browser-Zeit.
 * Behandelt Sommerzeit-Wechsel korrekt indem das Offset dynamisch ermittelt wird.
 */
export function parseBerlinTime(timeStr: string, now: Date = new Date()): Date {
  const cetDate = now.toLocaleDateString('en-CA', { timeZone: 'Europe/Berlin' });
  const padded = timeStr.padStart(5, '0');
  const berlinDate = new Date(`${cetDate}T${padded}:00`);
  const utcEquiv = new Date(berlinDate.toLocaleString('en-US', { timeZone: 'UTC' }));
  const berlinEquiv = new Date(berlinDate.toLocaleString('en-US', { timeZone: 'Europe/Berlin' }));
  const offsetMs = berlinEquiv.getTime() - utcEquiv.getTime();
  return new Date(berlinDate.getTime() - offsetMs);
}

interface SchedulableEntry {
  agentKey: AgentKey;
  time: Date;
  label: string;
}

/**
 * V1 `getNextScheduledEvent`: nimmt das EVE-Profil und gibt den nächsten anstehenden
 * Agent-Run zurück. Wenn alle heutigen Events vorbei sind, der erste Event von morgen
 * (Wochenenden werden übersprungen).
 */
export function getNextScheduledEvent(
  profile: EveProfile,
  now: Date = new Date(),
): ScheduledEvent {
  const isWeekend = now.getDay() === 0 || now.getDay() === 6;
  const agentSchedule = profile.agentSchedule ?? {};
  const agentTime = profile.agentTime ?? {};

  const scheduled = SCHEDULABLE_AGENTS.filter(
    (k) => agentSchedule[k] === 'auto' && agentTime[k],
  );
  if (scheduled.length === 0) {
    return { label: 'Kein Auto-Agent konfiguriert', agentKey: null, seconds: null };
  }

  const events: SchedulableEntry[] = scheduled.map((k) => ({
    agentKey: k,
    time: parseBerlinTime(agentTime[k]!, now),
    label: k.toUpperCase(),
  }));

  const upcoming = events
    .map((e) => ({ ...e, seconds: Math.floor((e.time.getTime() - now.getTime()) / 1000) }))
    .filter((e) => e.seconds > 0)
    .sort((a, b) => a.seconds - b.seconds);

  if (upcoming.length > 0) {
    return {
      label: upcoming[0]!.label,
      agentKey: upcoming[0]!.agentKey,
      seconds: upcoming[0]!.seconds,
    };
  }

  if (isWeekend) {
    return { label: 'WOCHENENDE — nächstes Event Montag', agentKey: null, seconds: null };
  }

  // Alle heutigen Events vorbei → ersten Event von morgen (Werktag) finden
  const first = [...events].sort((a, b) => {
    const aTime = agentTime[a.agentKey] ?? '07:00';
    const bTime = agentTime[b.agentKey] ?? '07:00';
    const [ah = 0, am = 0] = aTime.split(':').map(Number);
    const [bh = 0, bm = 0] = bTime.split(':').map(Number);
    return ah * 60 + am - (bh * 60 + bm);
  })[0]!;

  const next = parseBerlinTime(agentTime[first.agentKey]!, now);
  next.setDate(next.getDate() + 1);
  while (next.getDay() === 0 || next.getDay() === 6) {
    next.setDate(next.getDate() + 1);
  }

  return {
    label: first.label,
    agentKey: first.agentKey,
    seconds: Math.floor((next.getTime() - now.getTime()) / 1000),
  };
}

/**
 * Formatiert die Restzeit als "1h 30m" / "5m 30s" / "Läuft jetzt" für UI-Badges.
 * V1-Konvention.
 */
export function formatScheduledCountdown(seconds: number | null | undefined): string {
  if (seconds == null) return '—';
  if (seconds <= 0) return 'Läuft jetzt';
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${h}h ${m}m`;
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

/**
 * Bestimmt ob jetzt ein Agent triggern soll. V1-Verhalten: wenn `seconds` zu 0
 * oder darunter geht, läuft der Agent. Wir nutzen einen kleinen Toleranzbereich
 * (±30s) damit das Tick-Polling den Trigger nicht verpasst.
 */
export const TRIGGER_TOLERANCE_SECONDS = 30;

export function shouldTriggerNow(event: ScheduledEvent): boolean {
  if (event.seconds == null) return false;
  return event.seconds <= 0 && event.seconds >= -TRIGGER_TOLERANCE_SECONDS;
}
